import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const DISMISS_KEY = "desashop_pwa_prompt_dismissed";

/**
 * Custom PWA install prompt.
 * - On Chrome/Edge/Android: captures beforeinstallprompt and shows a native install
 * - On iOS Safari: shows a manual hint since iOS doesn't fire beforeinstallprompt
 */
export default function PWAInstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Detect iOS Safari
        const ua = window.navigator.userAgent;
        const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const isStandalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone;
        const dismissed = localStorage.getItem(DISMISS_KEY);

        if (isStandalone || dismissed) return;

        setIsIOS(iOS);

        function onBeforeInstall(e) {
            e.preventDefault();
            setDeferred(e);
            // Delay a bit so the user has interacted with the site first
            setTimeout(() => setVisible(true), 8000);
        }
        window.addEventListener("beforeinstallprompt", onBeforeInstall);

        // iOS fallback — show hint after 12s if we detect iOS and it's not standalone
        if (iOS) {
            const t = setTimeout(() => setVisible(true), 12000);
            return () => {
                clearTimeout(t);
                window.removeEventListener(
                    "beforeinstallprompt",
                    onBeforeInstall
                );
            };
        }

        return () =>
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
    }, []);

    async function handleInstall() {
        if (!deferred) return;
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        localStorage.setItem(DISMISS_KEY, "1");
        setDeferred(null);
        setVisible(false);
        if (outcome === "accepted") {
            // Optionally track
        }
    }

    function dismiss() {
        localStorage.setItem(DISMISS_KEY, "1");
        setVisible(false);
    }

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ y: 120, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 120, opacity: 0 }}
                    className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[55] bg-ink text-parchment border-2 border-ink shadow-manga-red p-4"
                    data-testid="pwa-install-prompt"
                >
                    <button
                        onClick={dismiss}
                        aria-label="Chiudi"
                        className="absolute top-2 right-2 w-7 h-7 border-2 border-parchment flex items-center justify-center hover:bg-bounty"
                        data-testid="pwa-dismiss-btn"
                    >
                        <X size={12} />
                    </button>
                    <div className="flex items-start gap-3 pr-6">
                        <div className="w-11 h-11 shrink-0 bg-bounty border-2 border-parchment flex items-center justify-center">
                            <Download size={20} />
                        </div>
                        <div>
                            <div className="font-hand text-lg text-gold -mb-1">
                                installa la ciurma
                            </div>
                            <div className="font-display text-xl tracking-tight">
                                DESASHOP SUL TELEFONO
                            </div>
                            {isIOS ? (
                                <p className="text-xs font-mono text-parchment/70 mt-2">
                                    Tocca{" "}
                                    <span className="font-bold text-gold">
                                        Condividi ↑
                                    </span>{" "}
                                    → poi{" "}
                                    <span className="font-bold text-gold">
                                        Aggiungi a Home
                                    </span>
                                </p>
                            ) : (
                                <>
                                    <p className="text-xs font-mono text-parchment/70 mt-2">
                                        Installa come app: navigazione più
                                        veloce, offline-ready, notifiche di
                                        pre-ordine.
                                    </p>
                                    <button
                                        onClick={handleInstall}
                                        className="btn-manga btn-manga-red mt-3 text-sm"
                                        data-testid="pwa-install-btn"
                                    >
                                        <Download
                                            size={14}
                                            className="mr-2"
                                        />
                                        Installa app
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
